/**
 * One door for every image that goes through the CDN.
 *
 * Call sites describe what they want in a small vocabulary (resize, cover,
 * max, quality) and never mention the vendor. The server injects the CDN's
 * switch, base URL and path names into the boot payload, so
 * TWICPICS_ENABLED=false on the server sends every image back to its origin
 * URL on the next page load, and a different vendor is a new adapter below
 * plus a config change. See docs/twicpics-images.md.
 */

export interface ImageTransforms {
    /** Scale to this width, keeping aspect ratio. */
    resize?: number;
    /** Crop-and-scale to exactly this box. */
    cover?: [number, number];
    /** Fit inside this box, never upscaling. */
    max?: [number, number];
    /** 1–100. */
    quality?: number;
}

export interface ImageCdnConfig {
    enabled: boolean;
    base: string;
    paths: { media: string; uploads: string | null };
}

const DEFAULTS: ImageCdnConfig = {
    enabled: true,
    base: 'https://fleetwire.twic.pics',
    paths: {media: 'web-media', uploads: 'uploads'},
};

/** Origins we front, and the CDN path name each maps to. */
const SOURCES: Array<{ host: string; prefix: string; path: keyof ImageCdnConfig['paths'] }> = [
    {host: 'fleetwire-main2.s3.us-west-1.amazonaws.com', prefix: '/media/', path: 'media'},
    {host: 's3.us-west-1.amazonaws.com', prefix: '/uploads.fleetwire.com/', path: 'uploads'},
    {host: 'uploads.fleetwire.com.s3.us-west-1.amazonaws.com', prefix: '/', path: 'uploads'},
];

export function imageCdnConfig(): ImageCdnConfig {
    const injected = window.__FW__?.imageCdn;

    if (!injected) {
        return DEFAULTS;
    }

    return {
        enabled: injected.enabled !== false,
        base: (injected.base || DEFAULTS.base).replace(/\/+$/, ''),
        paths: {
            media: injected.paths?.media || DEFAULTS.paths.media,
            uploads: injected.paths?.uploads ?? null,
        },
    };
}

/**
 * The origin file's key and which CDN path serves it, or null when the URL is
 * not one of ours (Turo's CDN, blob previews, placeholders, malformed input).
 */
export function resolveOrigin(url: string | null | undefined): { path: keyof ImageCdnConfig['paths']; key: string } | null {
    if (!url) return null;

    try {
        const parsed = new URL(url);
        const source = SOURCES.find((candidate) => parsed.hostname === candidate.host && parsed.pathname.startsWith(candidate.prefix));

        if (!source) return null;

        const key = parsed.pathname.slice(source.prefix.length);

        return key ? {path: source.path, key} : null;
    } catch {
        return null;
    }
}

/** The one place that knows TwicPics' URL grammar. */
function twicpicsAdapter(base: string, pathName: string, key: string, transforms: ImageTransforms): string {
    const parts: string[] = [];

    if (transforms.resize) parts.push(`resize=${transforms.resize}`);
    if (transforms.cover) parts.push(`cover=${transforms.cover[0]}x${transforms.cover[1]}`);
    if (transforms.max) parts.push(`max=${transforms.max[0]}x${transforms.max[1]}`);
    if (transforms.quality) parts.push(`quality=${transforms.quality}`);

    return `${base}/${pathName}/${key}?twic=v1${parts.length ? '/' + parts.join('/') : ''}`;
}

/**
 * The CDN URL for one of our images, or the original when the CDN is off or
 * the URL is not ours.
 */
export function cdnImage(url: string | null | undefined, transforms: ImageTransforms = {}): string {
    const source = url ?? '';
    const config = imageCdnConfig();

    if (!config.enabled) return source;

    const origin = resolveOrigin(source);

    if (!origin) return source;

    const pathName = config.paths[origin.path];

    if (!pathName) return source;

    return twicpicsAdapter(config.base, pathName, origin.key, transforms);
}

/**
 * A `srcset` for a fixed-width slot: the width at 1x and, at 2x, twice the
 * width bounded so the original is never upscaled. Undefined when the image
 * is not on the CDN, so the browser uses `src` alone.
 */
export function cdnSrcSet(url: string | null | undefined, width: number): string | undefined {
    const oneX = cdnImage(url, {resize: width});

    if (!url || oneX === url) return undefined;

    const twoX = cdnImage(url, {max: [width * 2, width * 2]});

    return `${oneX} 1x, ${twoX} 2x`;
}

/**
 * Accepts the older `['resize=375', 'quality=50']` form still used at a few
 * call sites and turns it into the vocabulary. Unknown entries are dropped
 * rather than passed through, so vendor syntax never leaks into a URL.
 */
export function transformsFromStrings(strings: string[]): ImageTransforms {
    const transforms: ImageTransforms = {};

    for (const entry of strings) {
        const [name, value = ''] = entry.split('=');
        const box = value.match(/^(\d+)x(\d+)$/);

        if (name === 'resize' && /^\d+$/.test(value)) transforms.resize = Number(value);
        else if (name === 'quality' && /^\d+$/.test(value)) transforms.quality = Number(value);
        else if (name === 'cover' && box) transforms.cover = [Number(box[1]), Number(box[2])];
        else if (name === 'max' && box) transforms.max = [Number(box[1]), Number(box[2])];
    }

    return transforms;
}

/**
 * Static assets we serve through the CDN by path name, and where each path's
 * files actually live (from the TwicPics dashboard). `images/` is the app's
 * own public/images folder, so its fallback is a relative URL on whatever
 * host the page is on; the others are absolute origins.
 */
const STATIC_ORIGINS: Record<string, string> = {
    'images': '/images/',
    'wp-image': 'https://fleetwire.io/wp-content/uploads/',
    'web-media': 'https://fleetwire-main2.s3.us-west-1.amazonaws.com/media/',
};

/**
 * A CDN URL for a static asset given as `<path name>/<file>`, e.g.
 * `images/logo/axle-logo.png`, or its origin when the CDN is off.
 */
export function cdnStatic(assetPath: string, transforms: ImageTransforms = {}): string {
    const clean = assetPath.replace(/^\/+/, '');
    const slash = clean.indexOf('/');
    const pathName = slash === -1 ? clean : clean.slice(0, slash);
    const key = slash === -1 ? '' : clean.slice(slash + 1);
    const origin = STATIC_ORIGINS[pathName];

    if (!origin || !key) {
        return assetPath;
    }

    const config = imageCdnConfig();

    if (!config.enabled) {
        return `${origin}${key}`;
    }

    return twicpicsAdapter(config.base, pathName, key, transforms);
}

/** Candidate widths for fluid storefront slots; the browser picks by `sizes` and DPR. */
export const STOREFRONT_WIDTHS = [400, 800, 1200, 1600];

/**
 * Card grids: one column on phones, two on tablets, three on desktop inside a
 * capped container beside the filter column, so a desktop card is ~340px no
 * matter how wide the window is. Stating that keeps a retina desktop on the
 * 800 candidate instead of reaching for 1200 or 1600.
 */
export const STOREFRONT_CARD_SIZES = '(max-width: 768px) 100vw, (max-width: 1024px) 45vw, 340px';

/** Single-column heroes and carousels, capped at their container. */
export const STOREFRONT_HERO_SIZES = '(max-width: 640px) 100vw, 640px';

/**
 * A width-descriptor `srcset` for a fluid slot, or undefined off the CDN.
 */
export function cdnWidthSrcSet(url: string | null | undefined, widths: number[] = STOREFRONT_WIDTHS): string | undefined {
    if (!url || cdnImage(url) === url) return undefined;

    return widths.map((width) => `${cdnImage(url, {resize: width})} ${width}w`).join(', ');
}

/**
 * A few-hundred-byte blurred stand-in for `lazy-src`, so a slot shows the
 * photo's colours while the real candidate downloads. Off the CDN there is
 * no cheap version, so callers keep their generic placeholder.
 */
export function cdnPlaceholder(url: string | null | undefined): string | undefined {
    if (!url || cdnImage(url) === url) return undefined;

    return cdnImage(url, {resize: 24, quality: 30});
}
